'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { useMediaDevices } from '@livekit/components-react';
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  LocalAudioTrack,
  LocalVideoTrack,
} from 'livekit-client';
import { toSafeDeviceIdConstraint } from '@/lib/media-capture-constraints';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Volume2,
  Settings,
  Camera,
  ImageIcon,
  Plus,
  Loader2,
  X,
} from 'lucide-react';
import { CrownIcon } from '@/components/ui/crown-icon';
import { useSettingsPersistence } from '@/hooks/useSettingsPersistence';
import { useOptionalHostSettings } from '@/lib/HostSettingsContext';
import { useOptionalRoomContext } from '@/lib/useOptionalRoomContext';
import {
  useCustomBackgroundImage,
  CUSTOM_BACKGROUND_PREFIX,
  CustomBackground,
} from '@/hooks/useCustomBackgroundImage';
import {
  applyBackgroundToVideoTrack,
  BACKGROUND_DEBOUNCE_MS,
} from '@/lib/background-processor';
import { useDebouncedValue } from '@/lib/useDebouncedValue';

interface DeviceSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
  defaultTab?: 'audio' | 'video' | 'backgrounds';
  onCancel?: () => void;
}

interface SettingsContentProps {
  activeTab: 'audio' | 'video' | 'backgrounds' | 'host';
  audioEnabled: boolean;
  setAudioEnabled: (enabled: boolean) => void;
  videoEnabled: boolean;
  setVideoEnabled: (enabled: boolean) => void;
  audioDeviceId: string;
  setAudioDeviceId: (deviceId: string) => void;
  videoDeviceId: string;
  setVideoDeviceId: (deviceId: string) => void;
  audioOutputDeviceId: string;
  setAudioOutputDeviceId: (deviceId: string) => void;
  audioInputDevices: any[];
  videoDevices: any[];
  audioOutputDevices: any[];
  audioLevel: number;
  isAnalyzingAudio: boolean;
  isPlayingTestSound: boolean;
  playTestSound: () => void;
  supportsSetSinkId: boolean;
  videoTrack: LocalVideoTrack | null;
  backgroundType: 'none' | 'blur' | 'image';
  virtualBackgroundImagePath: string;
  selectBackground: (type: 'none' | 'blur' | 'image', imagePath?: string) => void;
  customBackgrounds: CustomBackground[];
  uploadCustomBackground: (
    file: File,
  ) => Promise<{ success: boolean; message?: string; dataUrl?: string }>;
  removeCustomBackground: (image: string) => void;
  isUploadingCustomBackground: boolean;
  customBackgroundError: string | null;
  resetCustomBackgroundError: () => void;
  noiseSuppressionEnabled: boolean;
  setNoiseSuppressionEnabled: (enabled: boolean) => void;
  isNoiseSuppressionPending: boolean;
}

function SettingsContent({
  activeTab,
  audioEnabled,
  setAudioEnabled,
  videoEnabled,
  setVideoEnabled,
  audioDeviceId,
  setAudioDeviceId,
  videoDeviceId,
  setVideoDeviceId,
  audioOutputDeviceId,
  setAudioOutputDeviceId,
  audioInputDevices,
  videoDevices,
  audioOutputDevices,
  audioLevel,
  isAnalyzingAudio,
  isPlayingTestSound,
  playTestSound,
  supportsSetSinkId,
  videoTrack,
  backgroundType,
  virtualBackgroundImagePath,
  selectBackground,
  customBackgrounds,
  uploadCustomBackground,
  removeCustomBackground,
  isUploadingCustomBackground,
  customBackgroundError,
  resetCustomBackgroundError,
  noiseSuppressionEnabled,
  setNoiseSuppressionEnabled,
  isNoiseSuppressionPending,
}: SettingsContentProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      await uploadCustomBackground(file);
      event.target.value = '';
    },
    [uploadCustomBackground],
  );

  const handleOpenFileDialog = React.useCallback(() => {
    resetCustomBackgroundError();
    fileInputRef.current?.click();
  }, [resetCustomBackgroundError]);

  return (
    <div className="h-full">
      {activeTab === 'audio' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white mb-6">Áudio</h3>

          {/* Seção Microfone */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-white mb-3">Microfone</h4>
            <div className="space-y-3">
              <select
                value={audioDeviceId || ''}
                onChange={(e) => setAudioDeviceId(e.target.value)}
                className="w-full p-3 bg-neutral-800 border border-neutral-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {audioInputDevices.map((device: any, index: number) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microfone ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Indicador de nível de áudio - EXATAMENTE igual ao Prejoin */}
            <div className="space-y-2">
              <div className="flex items-center space-x-1">
                {[...Array(10)].map((_, i) => {
                  const threshold = (i + 1) / 10;
                  const isActive = audioLevel >= threshold;
                  const isLow = i < 3;
                  const isMedium = i >= 3 && i < 7;
                  const isHigh = i >= 7;

                  let colorClass = 'bg-neutral-600';
                  if (isActive) {
                    if (isLow) colorClass = 'bg-green-500';
                    else if (isMedium) colorClass = 'bg-yellow-500';
                    else if (isHigh) colorClass = 'bg-red-500';
                  }

                  return (
                    <div
                      key={i}
                      className={`h-2 w-3 rounded-sm transition-colors duration-150 ${colorClass}`}
                    />
                  );
                })}
              </div>
              <div className="text-xs text-neutral-400">Nível: {Math.round(audioLevel * 100)}%</div>
            </div>
          </div>

          {/* Seção Supressão de ruído */}
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="noise-suppression"
                  checked={noiseSuppressionEnabled}
                  onCheckedChange={(checked) => setNoiseSuppressionEnabled(checked === true)}
                  disabled={isNoiseSuppressionPending}
                  className="border-neutral-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
                <label
                  htmlFor="noise-suppression"
                  className="text-sm font-medium text-white cursor-pointer flex-1"
                >
                  Ativar supressão de ruído
                </label>
                {isNoiseSuppressionPending && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
              </div>
            </div>
          </div>

          {/* Seção Saída de áudio - EXATAMENTE igual ao Prejoin */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-white mb-3">Saída de áudio</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <select
                  value={audioOutputDeviceId || 'default'}
                  onChange={(e) => setAudioOutputDeviceId(e.target.value)}
                  className="flex-1 p-3 bg-neutral-800 border border-neutral-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="default">Padrão</option>
                  {audioOutputDevices.map((device: any, index: number) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Dispositivo de áudio ${index + 1}`}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={playTestSound}
                  disabled={isPlayingTestSound}
                  className={`px-4 py-3 rounded-lg border transition-colors text-sm ${
                    isPlayingTestSound
                      ? 'bg-blue-600 text-white border-blue-500 cursor-not-allowed'
                      : 'bg-neutral-700 hover:bg-neutral-600 text-white border-neutral-600'
                  }`}
                >
                  {isPlayingTestSound ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Reproduzindo...</span>
                    </div>
                  ) : (
                    'Tocar um som de teste'
                  )}
                </button>
              </div>

              {/* Informação sobre suporte ao setSinkId - EXATAMENTE igual ao Prejoin */}
              {!supportsSetSinkId && (
                <div className="p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
                  <p className="text-sm text-yellow-300">
                    ⚠️ Seu navegador não suporta seleção de dispositivo de saída específico. O som
                    de teste será reproduzido no dispositivo padrão do sistema.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'video' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white mb-6">Vídeo</h3>

          {/* Preview da câmera - EXATAMENTE igual ao Prejoin */}
          {videoTrack && (
            <div className="mb-6">
              <div className="relative w-full h-64 bg-neutral-900 rounded-lg overflow-hidden border border-neutral-700">
                <video
                  className="w-full h-full object-cover"
                  ref={(el) => {
                    if (el && videoTrack) {
                      videoTrack.attach(el);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Configurações de Câmera - EXATAMENTE igual ao Prejoin */}
          <div className="space-y-4">
            <div className="space-y-3">
              <label className="text-sm text-neutral-300">Dispositivo de vídeo:</label>
              <select
                value={videoDeviceId || ''}
                onChange={(e) => setVideoDeviceId(e.target.value)}
                className="w-full p-3 bg-neutral-800 border border-neutral-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {videoDevices.map((device: any, index: number) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Câmera ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'backgrounds' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white mb-6">Fundos virtuais</h3>

          {/* Preview do vídeo - EXATAMENTE igual ao Prejoin */}
          {videoTrack && (
            <div className="mb-6">
              <div className="relative w-full h-64 bg-neutral-900 rounded-lg overflow-hidden border border-neutral-700">
                <video
                  className="w-full h-full object-cover"
                  ref={(el) => {
                    if (el && videoTrack) {
                      videoTrack.attach(el);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Opções de fundo - EXATAMENTE igual ao Prejoin */}
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {/* Adicionar novo fundo */}
              <button
                type="button"
                className="relative group cursor-pointer p-3 rounded-lg border-2 border-dashed border-neutral-600 hover:border-neutral-500 hover:bg-neutral-800/40 transition-colors"
                onClick={handleOpenFileDialog}
              >
                <div className="aspect-video bg-neutral-800 rounded-md flex flex-col items-center justify-center gap-2 text-neutral-200">
                  {isUploadingCustomBackground ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-xs font-medium">Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      <span className="text-xs font-medium">Adicionar novo fundo</span>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </button>
              {/* Nenhum */}
              <button
                className={`relative group cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                  backgroundType === 'none'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-neutral-600 hover:border-neutral-500'
                }`}
                onClick={() => selectBackground('none')}
              >
                <div className="aspect-video bg-neutral-800 rounded-md flex items-center justify-center">
                  <div className="text-white text-xs font-medium">Nenhum</div>
                </div>
              </button>

              {/* Desfoque suave */}
              <button
                className={`relative group cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                  backgroundType === 'blur'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-neutral-600 hover:border-neutral-500'
                }`}
                onClick={() => selectBackground('blur')}
              >
                <div className="aspect-video bg-gradient-to-br from-blue-500 to-purple-600 rounded-md flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/20 blur-sm"></div>
                  <div className="text-white text-xs font-medium relative z-10">Desfoque</div>
                </div>
              </button>

              {customBackgrounds.map((background) => (
                <div key={background.id} className="relative">
                  <button
                    type="button"
                    className={`relative group cursor-pointer p-3 rounded-lg border-2 transition-colors ${
                      backgroundType === 'image' &&
                      virtualBackgroundImagePath === background.dataUrl
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-neutral-600 hover:border-neutral-500'
                    }`}
                    onClick={() => selectBackground('image', background.dataUrl)}
                  >
                    <div
                      className="aspect-video bg-cover bg-center rounded-md"
                      style={{ backgroundImage: `url(${background.dataUrl})` }}
                    >
                      <div className="w-full h-full bg-black/50 flex items-end justify-center p-1">
                        <span className="text-white text-xs font-medium">Personalizado</span>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="absolute top-1.5 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeCustomBackground(background.id);
                    }}
                    aria-label="Remover fundo personalizado"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            {customBackgroundError && (
              <p className="text-sm text-red-400">{customBackgroundError}</p>
            )}
            <p className="text-xs text-neutral-400">
              Formatos suportados: JPG e PNG. Tamanho máximo da imagem convertida: 5&nbsp;MB.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'host' && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white mb-6">Anfitrião</h3>

          <div className="space-y-4">
            <div className="bg-neutral-800 rounded-lg p-4">
              <h4 className="text-lg font-medium text-white mb-3">Gravação</h4>
              <p className="text-neutral-300 text-sm">
                Apenas o anfitrião ou co-anfitriões podem iniciar ou encerrar a gravação desta
                reunião.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DeviceSettingsModal({
  open,
  onOpenChange,
  trigger,
  defaultTab = 'audio',
  onCancel,
}: DeviceSettingsModalProps) {
  const { settings, saveSettings, isLoaded } = useSettingsPersistence();
  const hostSettings = useOptionalHostSettings();

  const room = useOptionalRoomContext();

  const [activeTab, setActiveTab] = useState<'audio' | 'video' | 'backgrounds' | 'host'>(
    defaultTab,
  );

  // Verificar se o usuário é anfitrião (apenas se room estiver disponível)
  const isHostFromMetadata = React.useMemo(() => {
    if (!room?.localParticipant?.metadata) return false;
    try {
      const metadata = JSON.parse(room.localParticipant.metadata);
      return metadata.role === 'host' || metadata.isHost === true;
    } catch (e) {
      return false;
    }
  }, [room?.localParticipant?.metadata]);

  const isHost = hostSettings?.isHost ?? isHostFromMetadata;

  const {
    customBackgrounds,
    isUploading: isUploadingCustomBackground,
    error: customBackgroundError,
    uploadCustomBackground,
    removeCustomBackground,
    resetError: resetCustomBackgroundError,
    resolveCustomBackgroundPath,
    getCustomBackgroundIdByDataUrl,
  } = useCustomBackgroundImage();

  const initialResolvedBackgroundPath = React.useMemo(
    () =>
      resolveCustomBackgroundPath(settings.virtualBackgroundImagePath) ??
      settings.virtualBackgroundImagePath ??
      '',
    [settings.virtualBackgroundImagePath, resolveCustomBackgroundPath],
  );

  // Atualizar aba ativa quando defaultTab mudar
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  // Capturar snapshot das configurações quando o modal abrir
  useEffect(() => {
    if (open && isLoaded) {
      const resolvedPath =
        resolveCustomBackgroundPath(settings.virtualBackgroundImagePath) ??
        settings.virtualBackgroundImagePath ??
        '';
      setOriginalSnapshot({
        audioEnabled: settings.audioEnabled,
        videoEnabled: settings.videoEnabled,
        audioDeviceId: settings.audioDeviceId,
        videoDeviceId: settings.videoDeviceId,
        audioOutputDeviceId: settings.audioOutputDeviceId,
        backgroundType: settings.backgroundType,
        virtualBackgroundImagePath: resolvedPath,
        noiseSuppressionEnabled: settings.noiseSuppressionEnabled,
      });
      setVirtualBackgroundImagePath(resolvedPath);
    }
  }, [open, isLoaded, settings, resolveCustomBackgroundPath]);

  // Estados locais para o modal
  const [audioEnabled, setAudioEnabled] = useState(settings.audioEnabled);
  const [videoEnabled, setVideoEnabled] = useState(settings.videoEnabled);
  const [audioDeviceId, setAudioDeviceId] = useState(settings.audioDeviceId);
  const [videoDeviceId, setVideoDeviceId] = useState(settings.videoDeviceId);
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState(settings.audioOutputDeviceId);

  // Estados locais para controlar supressão de ruído (para Prejoin)
  // Nota: Dentro da Room, o Krisp já está ativo via SettingsMenu > MicrophoneSettings
  const [localNoiseSuppression, setLocalNoiseSuppression] = useState(
    settings.noiseSuppressionEnabled ?? false,
  );
  const [localNoiseSuppressionPending, setLocalNoiseSuppressionPending] = useState(false);

  // Sincronizar estado inicial com configurações persistentes
  useEffect(() => {
    if (open && isLoaded) {
      setLocalNoiseSuppression(settings.noiseSuppressionEnabled ?? false);
    }
  }, [open, isLoaded, settings.noiseSuppressionEnabled]);

  // Função para alternar supressão de ruído
  const toggleNoiseSuppression = useCallback(() => {
    setLocalNoiseSuppressionPending(true);
    setTimeout(() => {
      setLocalNoiseSuppression((prev) => !prev);
      setLocalNoiseSuppressionPending(false);
    }, 300);
  }, []);

  // Estados para análise de áudio
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack | null>(null);
  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isAnalyzingAudio, setIsAnalyzingAudio] = useState(false);
  const [isPlayingTestSound, setIsPlayingTestSound] = useState(false);
  const [supportsSetSinkId, setSupportsSetSinkId] = useState(false);

  // Estados para fundos virtuais
  const [backgroundType, setBackgroundType] = useState<'none' | 'blur' | 'image'>(
    settings.backgroundType,
  );
  const [virtualBackgroundImagePath, setVirtualBackgroundImagePath] = useState<string>(
    initialResolvedBackgroundPath,
  );
  const [isApplyingBackground, setIsApplyingBackground] = useState(false);
  const debouncedBackgroundType = useDebouncedValue(backgroundType, BACKGROUND_DEBOUNCE_MS);
  const debouncedVirtualBackgroundImagePath = useDebouncedValue(
    virtualBackgroundImagePath,
    BACKGROUND_DEBOUNCE_MS,
  );

  // Snapshot das configurações originais quando o modal abrir
  const [originalSnapshot, setOriginalSnapshot] = useState({
    audioEnabled: settings.audioEnabled,
    videoEnabled: settings.videoEnabled,
    audioDeviceId: settings.audioDeviceId,
    videoDeviceId: settings.videoDeviceId,
    audioOutputDeviceId: settings.audioOutputDeviceId,
    backgroundType: settings.backgroundType,
    virtualBackgroundImagePath: initialResolvedBackgroundPath,
    noiseSuppressionEnabled: settings.noiseSuppressionEnabled,
  });

  // Refs para análise de áudio
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const analyserRef = React.useRef<AnalyserNode | null>(null);
  const animationFrameRef = React.useRef<number | null>(null);

  // Dispositivos de mídia
  const audioInputDevices = useMediaDevices({ kind: 'audioinput' });
  const videoDevices = useMediaDevices({ kind: 'videoinput' });
  const audioOutputDevices = useMediaDevices({ kind: 'audiooutput' });

  // Sincronizar com configurações persistentes
  useEffect(() => {
    if (isLoaded) {
      setAudioEnabled(settings.audioEnabled);
      setVideoEnabled(settings.videoEnabled);
      setAudioDeviceId(settings.audioDeviceId);
      setVideoDeviceId(settings.videoDeviceId);
      setAudioOutputDeviceId(settings.audioOutputDeviceId);
      setBackgroundType(settings.backgroundType);
      const resolvedPath =
        resolveCustomBackgroundPath(settings.virtualBackgroundImagePath) ??
        settings.virtualBackgroundImagePath ??
        '';
      setVirtualBackgroundImagePath(resolvedPath);
    }
  }, [settings, isLoaded, resolveCustomBackgroundPath]);

  useEffect(() => {
    const hasCustomBackgrounds = customBackgrounds.length > 0;
    const customDataUrls = customBackgrounds.map((item) => item.dataUrl);

    if (!hasCustomBackgrounds) {
      if (
        backgroundType === 'image' &&
        virtualBackgroundImagePath &&
        virtualBackgroundImagePath.startsWith('data:')
      ) {
        setBackgroundType('none');
        setVirtualBackgroundImagePath('');
      }
      return;
    }

    if (backgroundType === 'image') {
      if (!virtualBackgroundImagePath) {
        const fallback = customBackgrounds[customBackgrounds.length - 1]?.dataUrl;
        if (fallback) {
          setVirtualBackgroundImagePath(fallback);
        }
        return;
      }

      if (
        virtualBackgroundImagePath.startsWith('data:') &&
        !customDataUrls.includes(virtualBackgroundImagePath)
      ) {
        const fallback = customBackgrounds[customBackgrounds.length - 1]?.dataUrl;
        if (fallback) {
          setVirtualBackgroundImagePath(fallback);
        } else {
          setBackgroundType('none');
          setVirtualBackgroundImagePath('');
        }
      }
    }
  }, [backgroundType, customBackgrounds, virtualBackgroundImagePath]);

  // Selecionar primeiro dispositivo de vídeo se nenhum estiver selecionado
  useEffect(() => {
    if (open && isLoaded && videoDevices.length > 0) {
      // Se não há dispositivo selecionado, selecionar o primeiro automaticamente
      if (!videoDeviceId) {
        const firstDevice = videoDevices[0];
        if (firstDevice) {
          setVideoDeviceId(firstDevice.deviceId);
        }
      }
    }
  }, [open, isLoaded, videoDevices, videoDeviceId]);

  // Verificar suporte para setSinkId
  useEffect(() => {
    setSupportsSetSinkId('setSinkId' in HTMLAudioElement.prototype);
  }, []);

  // Função para analisar nível de áudio (mesma lógica do Prejoin)
  const analyzeAudioLevel = React.useCallback(() => {
    if (!audioTrack || !audioContextRef.current || !analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calcular o nível médio de áudio (mesma lógica do Prejoin)
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    const normalizedLevel = Math.min(average / 128, 1); // Normalizar para 0-1

    setAudioLevel(normalizedLevel);
  }, [audioTrack]);

  // Configurar análise de áudio
  useEffect(() => {
    if (audioTrack && audioTrack.mediaStream) {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(audioTrack.mediaStream);

      source.connect(analyser);
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8; // Mesma configuração do Prejoin

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const startAnalysis = () => {
        analyzeAudioLevel();
        animationFrameRef.current = requestAnimationFrame(startAnalysis);
      };

      startAnalysis();
      setIsAnalyzingAudio(true);

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        audioContext.close();
        audioContextRef.current = null;
        analyserRef.current = null;
        setIsAnalyzingAudio(false);
      };
    } else {
      setAudioLevel(0);
      setIsAnalyzingAudio(false);
    }
  }, [audioTrack, analyzeAudioLevel]);

  // Criar track de áudio quando dispositivo muda
  useEffect(() => {
    if (!open) {
      if (audioTrack) {
        audioTrack.stop();
        setAudioTrack(null);
      }
      return;
    }

    const createAudioTrack = async () => {
      try {
        // Parar track anterior se existir
        if (audioTrack) {
          audioTrack.stop();
          setAudioTrack(null);
        }

        const safeAudioDeviceId = toSafeDeviceIdConstraint(audioDeviceId);
        const captureOptions =
          safeAudioDeviceId !== undefined ? { deviceId: safeAudioDeviceId } : undefined;
        const newTrack = await createLocalAudioTrack(captureOptions);

        setAudioTrack(newTrack);
      } catch (error) {
        console.error('Erro ao criar track de áudio:', error);
      }
    };

    createAudioTrack();
  }, [audioDeviceId, audioEnabled, open]);

  // Criar track de vídeo quando dispositivo muda
  useEffect(() => {
    if (!open) {
      // Limpar track quando modal fecha
      if (videoTrack) {
        videoTrack.stop();
        setVideoTrack(null);
      }
      return;
    }

    const createVideoTrack = async () => {
      try {
        // Parar track anterior se existir
        if (videoTrack) {
          videoTrack.stop();
          setVideoTrack(null);
        }

        // Criar track apenas se vídeo estiver habilitado
        if (videoEnabled) {
          const safeVideoDeviceId = toSafeDeviceIdConstraint(videoDeviceId);
          const newTrack = await createLocalVideoTrack(
            safeVideoDeviceId !== undefined ? { deviceId: safeVideoDeviceId } : {},
          );

          setVideoTrack(newTrack);
        }
      } catch (error) {
        console.error('Erro ao criar track de vídeo:', error);
      }
    };

    createVideoTrack();
  }, [videoDeviceId, videoEnabled, open]);

  // Video track é conectado via ref nos elementos video

  // Função para selecionar fundo virtual (sem salvar automaticamente)
  const selectBackground = (type: 'none' | 'blur' | 'image', imagePath?: string) => {
    setBackgroundType(type);
    if (type === 'image' && imagePath) {
      setVirtualBackgroundImagePath(imagePath);
    } else if (type !== 'image') {
      setVirtualBackgroundImagePath((current) =>
        current && current.startsWith('data:') ? '' : current,
      );
    }
  };

  const handleUploadCustomBackground = async (file: File) => {
    const result = await uploadCustomBackground(file);
    if (result.success && result.dataUrl) {
      selectBackground('image', result.dataUrl);
    }
    return result;
  };

  const handleRemoveCustomBackground = (id: string) => {
    const remaining = customBackgrounds.filter((item) => item.id !== id);
    removeCustomBackground(id);
    if (backgroundType === 'image' && virtualBackgroundImagePath) {
      const stillExists = remaining.some((item) => item.dataUrl === virtualBackgroundImagePath);
      if (!stillExists) {
        if (remaining.length > 0) {
          setVirtualBackgroundImagePath(remaining[remaining.length - 1].dataUrl);
        } else {
          setBackgroundType('none');
          setVirtualBackgroundImagePath('');
        }
      }
    }
  };

  // Função para cancelar alterações e restaurar configurações originais
  const handleCancel = () => {
    // Restaurar estados locais para o snapshot original
    setAudioEnabled(originalSnapshot.audioEnabled);
    setVideoEnabled(originalSnapshot.videoEnabled);
    setAudioDeviceId(originalSnapshot.audioDeviceId);
    setVideoDeviceId(originalSnapshot.videoDeviceId);
    setAudioOutputDeviceId(originalSnapshot.audioOutputDeviceId);
    setBackgroundType(originalSnapshot.backgroundType);
    setVirtualBackgroundImagePath(originalSnapshot.virtualBackgroundImagePath);

    // Restaurar estado de supressão de ruído
    if (localNoiseSuppression !== originalSnapshot.noiseSuppressionEnabled) {
      toggleNoiseSuppression();
    }

    // Chamar callback personalizado primeiro (para Prejoin restaurar seus próprios estados)
    if (onCancel) {
      onCancel();
    }

    // Restaurar configurações persistentes após um pequeno delay para evitar conflitos
    setTimeout(() => {
      const persistedVirtualPath =
        originalSnapshot.backgroundType === 'image'
          ? getPersistedVirtualPath(originalSnapshot.virtualBackgroundImagePath)
          : '';
      saveSettings({
        audioEnabled: originalSnapshot.audioEnabled,
        videoEnabled: originalSnapshot.videoEnabled,
        audioDeviceId: originalSnapshot.audioDeviceId,
        videoDeviceId: originalSnapshot.videoDeviceId,
        audioOutputDeviceId: originalSnapshot.audioOutputDeviceId,
        backgroundType: originalSnapshot.backgroundType,
        virtualBackgroundImagePath: persistedVirtualPath,
        noiseSuppressionEnabled: originalSnapshot.noiseSuppressionEnabled,
      });
      window.dispatchEvent(
        new CustomEvent('settings-saved', {
          detail: {
            audioEnabled: originalSnapshot.audioEnabled,
            videoEnabled: originalSnapshot.videoEnabled,
            audioDeviceId: originalSnapshot.audioDeviceId,
            videoDeviceId: originalSnapshot.videoDeviceId,
            audioOutputDeviceId: originalSnapshot.audioOutputDeviceId,
            backgroundType: originalSnapshot.backgroundType,
            virtualBackgroundImagePath: persistedVirtualPath,
            noiseSuppressionEnabled: originalSnapshot.noiseSuppressionEnabled,
          },
        }),
      );
    }, 100);

    // Fechar modal
    onOpenChange(false);
  };

  // Aplicar fundos virtuais ao videoTrack do modal (apenas para preview)
  useEffect(() => {
    const processBackground = async () => {
      if (!videoTrack) {
        return;
      }

      if (!open) {
        return;
      }

      setIsApplyingBackground(true);
      const delay = (ms: number): Promise<void> =>
        new Promise((resolve) => setTimeout(resolve, ms));

      try {
        const targetType = debouncedBackgroundType;
        const targetImage =
          targetType === 'image' ? debouncedVirtualBackgroundImagePath : undefined;
        const shouldApplyProcessor = targetType === 'blur' || targetType === 'image';

        await applyBackgroundToVideoTrack(
          videoTrack,
          {
            targetType,
            targetImage,
            shouldApplyProcessor,
            delay,
            notifyOnError: false,
          },
        );
      } finally {
        setIsApplyingBackground(false);
      }
    };

    void processBackground();
  }, [
    videoTrack,
    debouncedBackgroundType,
    debouncedVirtualBackgroundImagePath,
    open,
  ]);

  // Função para tocar som de teste - EXATAMENTE igual ao Prejoin
  const playTestSound = async () => {
    if (isPlayingTestSound) return;

    try {
      setIsPlayingTestSound(true);

      // Criar contexto de áudio e configurar oscilador
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const destination = audioContext.createMediaStreamDestination();

      // Conectar os nós
      oscillator.connect(gainNode);
      gainNode.connect(destination);

      // Configurar oscilador
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime); // Lá (440 Hz)
      oscillator.type = 'sine';

      // Configurar envelope (fade in/out)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.9);

      // Criar elemento de áudio para reprodução
      const audio = new Audio();
      const stream = destination.stream;
      audio.srcObject = stream;
      audio.volume = 0.3;

      // Configurar o dispositivo de saída específico - FUNCIONALIDADE PRINCIPAL
      if (audioOutputDeviceId !== 'default' && 'setSinkId' in audio) {
        try {
          await (audio as any).setSinkId(audioOutputDeviceId);
        } catch (error) {
          console.warn('Não foi possível configurar dispositivo específico, usando padrão:', error);
        }
      }

      // Tocar o som
      oscillator.start(audioContext.currentTime);
      await audio.play();

      // Parar após 1 segundo
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
        setIsPlayingTestSound(false);
      }, 1000);
    } catch (error) {
      console.error('Erro ao reproduzir som de teste:', error);
      setIsPlayingTestSound(false);

      // Fallback: usar um som simples com setSinkId - IGUAL AO PREJOIN
      try {
        const audio = new Audio();
        audio.src =
          'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
        audio.volume = 0.3;

        // Tentar configurar dispositivo de saída no fallback também
        if (audioOutputDeviceId !== 'default' && 'setSinkId' in audio) {
          try {
            await (audio as any).setSinkId(audioOutputDeviceId);
          } catch (sinkError) {
            console.warn('Erro ao configurar dispositivo no fallback:', sinkError);
          }
        }

        await audio.play();

        setTimeout(() => {
          setIsPlayingTestSound(false);
        }, 2000);
      } catch (fallbackError) {
        console.error('Erro no fallback do som de teste:', fallbackError);
        setIsPlayingTestSound(false);
      }
    }
  };

  // Funções para alterar configurações (sem salvar automaticamente)
  const handleAudioEnabledChange = (enabled: boolean) => {
    setAudioEnabled(enabled);
  };

  const handleVideoEnabledChange = (enabled: boolean) => {
    setVideoEnabled(enabled);
  };

  const handleAudioDeviceChange = (deviceId: string) => {
    setAudioDeviceId(deviceId);
  };

  const handleVideoDeviceChange = (deviceId: string) => {
    setVideoDeviceId(deviceId);
  };

  const handleAudioOutputDeviceChange = (deviceId: string) => {
    setAudioOutputDeviceId(deviceId);
  };

  const getPersistedVirtualPath = React.useCallback(
    (path: string) => {
      if (!path) return '';
      if (!path.startsWith('data:')) return path;
      const id = getCustomBackgroundIdByDataUrl(path);
      return id ? `${CUSTOM_BACKGROUND_PREFIX}${id}` : path;
    },
    [getCustomBackgroundIdByDataUrl],
  );

  // Função para salvar todas as configurações
  const handleSaveSettings = () => {
    const persistedVirtualPath =
      backgroundType === 'image' ? getPersistedVirtualPath(virtualBackgroundImagePath) : '';

    saveSettings({
      audioEnabled,
      videoEnabled,
      audioDeviceId,
      videoDeviceId,
      audioOutputDeviceId,
      backgroundType,
      virtualBackgroundImagePath: persistedVirtualPath,
      noiseSuppressionEnabled: localNoiseSuppression,
    });

    // Disparar evento customizado para forçar sincronização imediata
    window.dispatchEvent(
      new CustomEvent('settings-saved', {
        detail: {
          audioEnabled,
          videoEnabled,
          audioDeviceId,
          videoDeviceId,
          audioOutputDeviceId,
          backgroundType,
          virtualBackgroundImagePath: persistedVirtualPath,
          noiseSuppressionEnabled: localNoiseSuppression,
        },
      }),
    );

    onOpenChange(false);
  };

  // Limpar tracks quando modal fecha
  useEffect(() => {
    if (!open) {
      if (audioTrack) {
        audioTrack.stop();
        setAudioTrack(null);
      }
      if (videoTrack) {
        videoTrack.stop();
        setVideoTrack(null);
      }
    }
  }, [open, audioTrack, videoTrack]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && trigger}

      <DialogContent className="max-w-4xl sm:max-w-4xl bg-black border-neutral-700 text-white p-0 h-[80vh] max-h-[600px] flex flex-col">
        <DialogHeader className="sr-only">
          <DialogTitle>Configurações</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Menu lateral */}
          <div className="w-64 bg-neutral-900 border-r border-neutral-700 p-6 flex flex-col">
            <h2 className="text-xl font-semibold text-white mb-6">Configurações</h2>
            <nav className="space-y-2">
              <button
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                  activeTab === 'audio'
                    ? 'bg-blue-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
                onClick={() => setActiveTab('audio')}
              >
                <Mic className="w-5 h-5" />
                <span>Áudio</span>
              </button>
              <button
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                  activeTab === 'video'
                    ? 'bg-blue-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
                onClick={() => setActiveTab('video')}
              >
                <Camera className="w-5 h-5" />
                <span>Vídeo</span>
              </button>
              <button
                className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                  activeTab === 'backgrounds'
                    ? 'bg-blue-600 text-white'
                    : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                }`}
                onClick={() => setActiveTab('backgrounds')}
              >
                <ImageIcon className="w-5 h-5" />
                <span>Fundos</span>
              </button>
              {isHost && (
                <button
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    activeTab === 'host'
                      ? 'bg-blue-600 text-white'
                      : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                  }`}
                  onClick={() => setActiveTab('host')}
                >
                  <CrownIcon className="w-5 h-5" />
                  <span>Anfitrião</span>
                </button>
              )}
            </nav>
          </div>

          {/* Conteúdo principal */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Área de conteúdo com scroll */}
            <div className="flex-1 p-6 overflow-y-auto">
              <SettingsContent
                activeTab={activeTab}
                audioEnabled={audioEnabled}
                setAudioEnabled={handleAudioEnabledChange}
                videoEnabled={videoEnabled}
                setVideoEnabled={handleVideoEnabledChange}
                audioDeviceId={audioDeviceId}
                setAudioDeviceId={handleAudioDeviceChange}
                videoDeviceId={videoDeviceId}
                setVideoDeviceId={handleVideoDeviceChange}
                audioOutputDeviceId={audioOutputDeviceId}
                setAudioOutputDeviceId={handleAudioOutputDeviceChange}
                audioInputDevices={audioInputDevices || []}
                videoDevices={videoDevices || []}
                audioOutputDevices={audioOutputDevices || []}
                audioLevel={audioLevel}
                isAnalyzingAudio={isAnalyzingAudio}
                isPlayingTestSound={isPlayingTestSound}
                playTestSound={playTestSound}
                supportsSetSinkId={supportsSetSinkId}
                videoTrack={videoTrack}
                backgroundType={backgroundType}
                virtualBackgroundImagePath={virtualBackgroundImagePath}
                selectBackground={selectBackground}
                customBackgrounds={customBackgrounds}
                uploadCustomBackground={handleUploadCustomBackground}
                removeCustomBackground={handleRemoveCustomBackground}
                isUploadingCustomBackground={isUploadingCustomBackground}
                customBackgroundError={customBackgroundError}
                resetCustomBackgroundError={resetCustomBackgroundError}
                noiseSuppressionEnabled={localNoiseSuppression}
                setNoiseSuppressionEnabled={toggleNoiseSuppression}
                isNoiseSuppressionPending={localNoiseSuppressionPending}
              />
            </div>

            {/* Botões de ação fixos no rodapé - EXATAMENTE igual ao Prejoin */}
            <div className="flex justify-end gap-3 p-6 pt-4 border-t border-neutral-700 bg-black">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="px-4 py-1.5 text-neutral-300 border-neutral-600 hover:bg-neutral-800 hover:text-white text-sm"
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSaveSettings}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm"
              >
                Salvar alterações
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
